/**
 * i18n bootstrap
 *
 * Initializes i18next and keeps luxon's locale in step with it.
 *
 * Deliberately provider-free: `initReactI18next` registers the instance
 * globally, so `useTranslation()` re-renders consumers on a language change
 * without any Context in the component tree, and `t` imported from here works
 * in plain modules (util/, hooks/) that never see React at all.
 *
 * Usage:
 *   import { t } from '../i18n';                 // non-component code
 *   const { t } = useTranslation('reports');     // components — re-renders
 */

import i18next from 'i18next';
import { Settings as LuxonSettings } from 'luxon';
import { initReactI18next } from 'react-i18next';

import { apiPost } from '../util/api';
import { getLanguage, setLanguage } from '../util/storage';

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SYSTEM_LANGUAGE,
  resolveLanguage,
} from './detectLocale';
import en from './locales/en';
import es from './locales/es';

const resources = { en, es };

/** Namespace list, derived from the English catalogs so the two cannot drift. */
export const NAMESPACES = Object.keys(en);

/**
 * Keep date formatting in the same language as the UI copy.
 *
 * Guarded because this runs at import time: syncing a date library's default is
 * a convenience, and it must never be able to take down module loading. Test
 * files that stub luxon with a partial mock are the realistic way `Settings`
 * goes missing.
 */
function applyLocaleSideEffects(language) {
  if (LuxonSettings) {
    LuxonSettings.defaultLocale = language;
  }
}

// Initialize synchronously at import, in the default language.
//
// Catalogs are bundled, so there is nothing to wait for and init settles
// immediately. Doing it here rather than inside initI18n() means `t()` returns
// real copy the moment any module imports it — before the async language
// resolution has finished, and in unit tests that render a single component
// without booting the app. Picking the *user's* language is the async part, and
// that is what initI18n() below does.
i18next.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: NAMESPACES,
  defaultNS: 'common',

  // A key missing from es/ must render its English value, never a blank or the
  // raw key. This is what makes shipping partial translations safe.
  returnEmptyString: false,

  // React Native has no HTML sink, and escaping would mangle the accents and
  // quotes that Spanish copy is full of.
  interpolation: { escapeValue: false },

  // Without this, components suspend while catalogs "load" — but catalogs are
  // bundled synchronously, and there is no Suspense boundary above the
  // navigator to catch it.
  react: { useSuspense: false },
});

applyLocaleSideEffects(DEFAULT_LANGUAGE);

/**
 * Resolve and activate the user's language.
 *
 * Awaited from App.js's existing font-loading gate so the first paint is
 * already in the right language — there is no flash of English.
 *
 * Safe to call more than once; subsequent calls just re-resolve.
 *
 * @returns {Promise<string>} the language that was activated
 */
export async function initI18n() {
  const stored = await getLanguage();
  const language = resolveLanguage(stored);

  if (i18next.language !== language) {
    await i18next.changeLanguage(language);
  }

  applyLocaleSideEffects(language);
  return language;
}

/**
 * Change the active language and persist the choice.
 *
 * @param {string} language - 'en', 'es', or 'system' to follow the device
 * @returns {Promise<string>} the language that was activated
 */
export async function setAppLanguage(language) {
  await setLanguage(language);

  const active = resolveLanguage(language);
  await i18next.changeLanguage(active);
  applyLocaleSideEffects(active);

  syncLanguageToServer(language);

  return active;
}

/**
 * Mirror the preference to the server so push notifications and emails — which
 * are composed server-side and read by this user — arrive in their language.
 *
 * Deliberately not awaited and never rethrows: the in-app language has already
 * changed by this point, and a failed sync only means notifications keep
 * arriving in English until the next successful change. Signed-out users (the
 * login screen has a picker too) simply have nothing to sync to.
 */
function syncLanguageToServer(language) {
  apiPost('UserManagement', '/users/language', { body: { language } }).catch((error) => {
    console.warn('Could not sync language preference:', error?.message || error);
  });
}

/** The language currently rendering ('en' | 'es'). */
export function getActiveLanguage() {
  return i18next.language || DEFAULT_LANGUAGE;
}

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, SYSTEM_LANGUAGE };

/**
 * Re-exported so components import their translation hook from here rather than
 * straight from react-i18next.
 *
 * This is not cosmetic: i18next is configured by this module's import-time side
 * effect. A component that imports `useTranslation` directly from react-i18next
 * pulls in no such side effect, so if nothing else in its import graph reaches
 * this file — which is exactly the case when a unit test renders one component
 * in isolation — `t` resolves against an unconfigured instance and renders bare
 * key names. Importing from here makes initialization part of the dependency.
 */
export { Trans, useTranslation } from 'react-i18next';

/**
 * Translate outside of React.
 *
 * Delegates on every call rather than capturing `i18next.t`: init() replaces
 * that method, so a reference captured at import time would keep resolving
 * against the pre-init instance and return bare keys forever.
 */
export const t = (...args) => i18next.t(...args);

export default i18next;
